import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { DocumentsService } from '../../../../../core/services';
import { DocumentsContextService } from '@app/core/services/context/factory';
import { IDocumentResponseDTO } from '@app/core/model';
import { PlatformService } from '@app/core/services/context/platform.service';
import { FeatureToggleService, LineOfBusiness, ProductNames } from '@mynexus/mynexus-ui-lib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-attachments-shared',
  templateUrl: './attachments-shared.component.html',
  styleUrls: ['./attachments-shared.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AttachmentsSharedComponent implements OnChanges, OnDestroy {
  @Input()
  get documents(): IDocumentResponseDTO[] {
    // eslint-disable-next-line no-underscore-dangle
    return this._documents; // BaseFile[]>();
  }
  set documents(value: IDocumentResponseDTO[]) {
    // eslint-disable-next-line no-underscore-dangle
    this._documents = value?.map(v => {
      return {
        ...v,
        createdOnDisplay: this.documentsService.utcToLocal(v.createdOn),
      }
    });
  }

  @Input() authorizationByProductId: string;
  @Output() selectedDocuments = new EventEmitter<any[]>(); // BaseFile[]>();

  selected!: any[]; // BaseFile[];
  isLoading = false;
  isPacCommercial = false;
  fileSubscription!: Subscription;
  selectedRowIndex!: number;
  documentsPreviewed: number[] = [];

  readonly letter = 'Letter';
  private _documents: IDocumentResponseDTO[]; // BaseFile[]>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly documentsService = inject(DocumentsService);
  private readonly documentsContextService = inject(DocumentsContextService);
  private readonly messageService = inject(MessageService);
  private readonly platformService = inject(PlatformService);
  private readonly ref = inject(ChangeDetectorRef);
  private readonly featureToggleService = inject(FeatureToggleService);
  private pacProductAndLobMigration = false;

  constructor() {
    this.featureToggleService.getFeatureAccess('PacProductAndLobMigration')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.pacProductAndLobMigration = isEnabled;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const documents = changes['documents']?.currentValue;
    if (documents) {
      const isPacCommercial = this.pacProductAndLobMigration 
        ? this.platformService.widgetContext === ProductNames.PACIM && this.platformService?.lineOfBusiness == LineOfBusiness.Commercial
        : this.platformService.widgetContext === ProductNames.PacCommercial;
      if (isPacCommercial) {
        this.documents = this.documents.filter(x => x.createdBy !== "ACMP-automated");
        this.isPacCommercial = true;
      }

      const docsForFaxDocs: any[] /*BaseFile[]*/ = this.documents ? [...this.documents] : [];

      if (docsForFaxDocs.length) {
        this.documents = this.documentsService.formatForTableDisplay(docsForFaxDocs);
      }
    }
  }

  ngOnDestroy() {
    if (this.fileSubscription) {
      this.fileSubscription.unsubscribe();
    }
  }

  onRowToggle() {
    this.selectedDocuments.emit(this.selected);
  }

  preview(event: Event, item: any/*BaseFile*/, rowIndex: number) {
    event.preventDefault();
    if (this.isPacCommercial && item.documentSource === this.letter) {
      return;
    }

    if (this.documentsPreviewed.indexOf(rowIndex) == -1) {
      this.documentsPreviewed.unshift(rowIndex);
    }

    if (this.fileSubscription) {
      this.fileSubscription.unsubscribe();
    }

    this.isLoading = true;
    this.selectedRowIndex = rowIndex;
    this.fileSubscription = this.documentsContextService
      .getFileById(
        item.tenantId,
        this.authorizationByProductId,
        item.docMetaDataId,
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.ref.markForCheck();
        }),
      )
      .subscribe(res => {
        const file: any/*FileExt*/ = res;

        if (file.fileContent) {
          this.previewFile(file);
        } else {
          this.messageService.add({
            severity: 'error',
            detail: `The document ${file.fileName} is no longer available .`,
            life: 5000
          });
        }
      });
  }

  documentPreviewed(rowIndex: number): boolean {
    if (!this.documentsPreviewed) return false;
    return (this.documentsPreviewed.indexOf(rowIndex) != -1);
  }

  previewFile(file: any/*BaseFile*/): void {
    this.documentsService.previewFileFaxDocs(file);
  }
}
